import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Camera, CameraResultType, CameraSource, ImageOptions } from '@capacitor/camera';
import { ActionSheetController, ModalController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';

import { endpoints } from 'src/app/constant/endpoints';
import { ApiService } from 'src/app/provider/api.service';

@Component({
  selector: 'app-post-modal',
  templateUrl: './post-modal.page.html',
  styleUrls: ['./post-modal.page.scss'],
  standalone: false,
})
export class PostModalPage implements OnInit {
  @Input() customer_id?: any;
  loading: boolean = false;
  attachment?: any;
  attachment_img?: any;
  postForm: FormGroup;
  imageOptionSetting: ImageOptions = {
    quality: 100,
    width: 720,
    saveToGallery: false,
    allowEditing: false,
    source: CameraSource.Camera,
    resultType: CameraResultType.Base64,
  };
  imageSettingGallery: ImageOptions = {
    quality: 100,
    allowEditing: false,
    source: CameraSource.Photos,
    resultType: CameraResultType.Uri,
  };

  constructor(
    private formBuilder: FormBuilder,
    public modalCtrl: ModalController,
    private actionSheetCtrl: ActionSheetController,
    private toastCtrl: ToastController,
    private translate: TranslateService,
    private apiService: ApiService,
  ) {
    this.postForm = this.formBuilder.group({
      customer_id: new FormControl(this.customer_id, Validators.compose([Validators.required])),
      content: new FormControl('', Validators.compose([Validators.required, Validators.minLength(1)])),
      attachment: new FormControl(null),
    });
  }

  ngOnInit() {
    if (this.customer_id) {
      this.postForm.get('customer_id')?.setValue(this.customer_id);
    }
  }

  async openCameraORGallery() {
    const actionSheet = await this.actionSheetCtrl?.create({
      buttons: [
        {
          text: this.translate?.instant('choose_from_gallery'),
          icon: 'image',
          handler: () => {
            this.pickImage();
          },
        },
        {
          text: this.translate?.instant('capture_image'),
          icon: 'camera',
          handler: () => {
            this.selectImage();
          },
        },
      ],
    });
    await actionSheet.present();
  }

  async selectImage() {
    try {
      const imageData = await Camera.getPhoto(this.imageOptionSetting);
      if (imageData.base64String) {
        this.attachment = `data:image/jpeg;base64,${imageData.base64String}`;
        this.attachment_img = `data:image/jpeg;base64,${imageData.base64String}`;
        this.postForm.get('attachment')?.setValue(imageData.base64String);
      } else {
        console.warn('Camera returned no base64 string.');
      }
    } catch (err) {
      console.error('Error selecting image from camera:', err);
      this.toastMessage(this.translate?.instant('image_capture_failed'));
    }
  }

  async pickImage() {
    try {
      const { photos } = await Camera.pickImages(this.imageSettingGallery);
      if (photos && photos.length > 0) {
        const photo = photos[0];
        if (photo && photo.webPath) {
          const response = await fetch(photo.webPath);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            this.attachment = reader.result as string;
            this.attachment_img = reader.result as string;
            this.postForm.get('attachment')?.setValue(reader.result);
          };
        } else {
          console.warn('Gallery returned no webPath for the photo.');
        }
      }
    } catch (err) {
      console.error('Error picking image from gallery:', err);
      this.toastMessage(this.translate?.instant('image_selection_failed'));
    }
  }

  deleteImage = () => {
    this.attachment = null;
    this.attachment_img = null;
    this.postForm.get('attachment')?.setValue(null);
  };

  onSubmit = () => {
    if (this.postForm.invalid) {
      this.toastMessage(this.translate?.instant('please_fill_all_required_fields'));
      console.warn('Form is invalid:', this.postForm.errors);
      return;
    }

    this.loading = true;
    this.apiService
      .post(endpoints.posts, this.postForm.value)
      .pipe(first())
      .subscribe(
        async (response: any) => {
          this.loading = false;

          if (response.success) {
            this.modalCtrl?.dismiss(true, 'success');
          } else {
            this.modalCtrl?.dismiss(false, 'error');
          }
          this.toastMessage(response.message);
        },
        (err) => {
          this.loading = false;
          console.error('API Error:', err);
          this.modalCtrl?.dismiss(false, 'error');
          this.toastMessage(this.translate?.instant('api_error_occurred'));
        },
      );
  };

  closeModal = () => {
    this.modalCtrl?.dismiss(false, 'cancel');
  };

  async toastMessage(msg: string) {
    await this.toastCtrl
      .create({
        message: msg,
        duration: 4000,
        position: 'top',
        color: 'primary',
      })
      .then((toastElem: any) => toastElem.present());
  }
}
